// TypeScript code for a SwiperMC component that utilizes the framer-motion and swiper libraries.
// The component displays a swiper with customizable slides and navigation buttons.

import { Skeleton } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import ISwiper from "swiper";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";
import cx from "classnames";

/**
 * Interface for the SwiperMC component.
 */
interface ISwiperMC {
  data: any[];
  wrapperClassName?: string;
  className?: string;
  classNameSlide?: string;
  render: (payload: any, index: number) => React.ReactNode;
  lessThanHowManyDataShouldHideNavigation?: number;
  loading?: boolean;
  howManySkeleton?: number;
  onReachEnd?: any;
  props?: SwiperProps;
  hideNavigation?: boolean;
  onClickNext?: () => void;
  alwaysShowNavigation?: boolean;
  showNavigationOnMobile?: boolean;
  navigationClassName?: string;
  smallerArrow?: boolean;
}

/**
 * Type for the SwiperMC component with additional props.
 */
type SwiperMCWithProps = ISwiperMC & {
  [key: string]: any;
};

/**
 * SwiperMC component.
 */
export default function SwiperMC({
  className,
  data,
  classNameSlide,
  wrapperClassName,
  render,
  lessThanHowManyDataShouldHideNavigation = 0,
  loading = false,
  howManySkeleton = 0,
  onReachEnd = undefined,
  props,
  hideNavigation,
  onClickNext,
  alwaysShowNavigation,
  showNavigationOnMobile,
  navigationClassName,
  smallerArrow,
}: SwiperMCWithProps) {
  const [currentSlide, currentSlideSet] = useState<number | undefined>(0);
  const swiperRef = useRef<ISwiper | null>(null);
  return (
    <motion.div
      whileHover="hoverNavigation"
      className={`relative ${wrapperClassName}`}
    >
      <Swiper
        speed={1000}
        onSlideChange={(e) => currentSlideSet(e.activeIndex)}
        onBeforeInit={(swiper) => (swiperRef.current = swiper)}
        className={className}
        initialSlide={currentSlide}
        onReachEnd={onReachEnd}
        {...props}
      >
        {!loading &&
          data?.map((item: any, idx: number) => (
            <SwiperSlide key={`${idx}`} className={classNameSlide}>
              {render(item, idx)}
            </SwiperSlide>
          ))}
        {loading &&
          new Array(howManySkeleton)
            .fill(undefined)
            .map((item: any, idx: number) => (
              <SwiperSlide key={`${idx}`} className={classNameSlide}>
                <div className="flex flex-col gap-1">
                  <Skeleton.Input active size="large" />
                  <Skeleton.Input active size="large" />
                  <Skeleton.Input active size="large" />
                </div>
              </SwiperSlide>
            ))}
      </Swiper>
      {!hideNavigation &&
        data &&
        data.length !== 0 &&
        data.length > lessThanHowManyDataShouldHideNavigation &&
        !loading && (
          <div
            className={cx(
              "justify-between absolute top-0 left-0 w-full h-full items-center flex",
              navigationClassName,
              { hidden: isMobile && !showNavigationOnMobile }
            )}
          >
            <motion.div
              variants={{
                hoverNavigation: {
                  opacity: 1,
                  scale: 1,
                  x: 0,
                  y: 0,
                },
              }}
              initial={
                !alwaysShowNavigation ? { opacity: 0, x: 8, scale: 0.6 } : {}
              }
              transition={{
                bounce: 0,
                ease: "easeOut",
                stiffness: 80,
                duration: 0.3,
                bounceDamping: 0,
                bounceStiffness: 0,
              }}
              onClick={() => swiperRef.current?.slidePrev(1000)}
              className={cx(
                `elL bg-white z-10 rounded-full flex justify-center items-center cursor-pointer hover:bg-opacity-50`,
                currentSlide === 0 && "bg-opacity-50",
                smallerArrow ? "w-6 h-6 -ml-2" : "-ml-4 w-8 h-8"
              )}
            >
              <HiChevronLeft
                className={`${swiperRef.current?.isBeginning && "opacity-30"}`}
                size={smallerArrow ? 20 : 28}
                color="black"
              />
            </motion.div>
            <AnimatePresence>
              <motion.div
                key="prev"
                variants={{
                  hoverNavigation: {
                    opacity: 1,
                    scale: 1,
                    x: 0,
                    y: 0,
                  },
                }}
                initial={
                  !alwaysShowNavigation ? { opacity: 0, x: -8, scale: 0.6 } : {}
                }
                transition={{
                  bounce: 0,
                  ease: "easeOut",
                  stiffness: 80,
                  duration: 0.3,
                  bounceDamping: 0,
                  bounceStiffness: 0,
                }}
                onClick={() => {
                  swiperRef.current?.slideNext(1000);
                  onClickNext && onClickNext();
                }}
                className={cx(
                  `elR bg-white z-10 rounded-full flex justify-center items-center cursor-pointer hover:bg-opacity-50`,
                  smallerArrow ? "w-6 h-6 -mr-2" : "-mr-4 w-8 h-8"
                )}
              >
                <HiChevronRight
                  className={`${swiperRef.current?.isEnd && "opacity-30"}`}
                  size={smallerArrow ? 20 : 28}
                  color="black"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        )}
    </motion.div>
  );
}
